import { v4 as uuidv4 } from 'uuid';
import { ProgramCategory, QualificationLevel, Program, Student, Document, ExamType, ExamResult } from './db/types';
import { DEFAULT_PROGRAM_DURATIONS, PROGRAM_LISTS } from './db/constants';
import { studentSupabaseService } from './db/supabase/studentSupabaseService';
import { teacherSupabaseService } from './db/supabase/teacherSupabaseService';
import { timetableSupabaseService } from './db/supabase/timetableSupabaseService';
import { timetableFileStorageService } from './db/timetable/fileStorageService';
import { adminFileStorageService } from './db/admins/fileStorageService';
import { unifiedStudentStorage } from './storage/unifiedStudentStorage';

// Export types from db/types.ts to ensure consistency
export type { ProgramCategory, QualificationLevel, Program, Student, Document, ExamType, ExamResult } from './db/types';

// Export constants for use throughout the application
export { DEFAULT_PROGRAM_DURATIONS, PROGRAM_LISTS } from './db/constants';

// Initialize local storage with empty data if empty
const initializeLocalStorage = () => {
  // Initialize unified student storage
  unifiedStudentStorage.initialize().catch(error => {
    console.error('Failed to initialize unified student storage:', error);
  });
  
  // Initialize with empty teachers array if not exists
  if (!localStorage.getItem('nalc_teachers')) {
    const emptyTeachers: any[] = [];
    localStorage.setItem('nalc_teachers', JSON.stringify(emptyTeachers));
  }

  // Initialize with empty admins array if not exists
  if (!localStorage.getItem('nalc_admins')) {
    const emptyAdmins: any[] = [];
    localStorage.setItem('nalc_admins', JSON.stringify(emptyAdmins));
  }
};

// Initialize when the module is loaded
initializeLocalStorage();

// Database service with unified student storage for 5000+ capacity
export const dbService = {
  // Student operations using unified storage
  getAllStudents: async (): Promise<Student[]> => {
    try {
      console.log('📚 Getting all students via unified storage...');
      
      // Get from unified storage (handles cache, compression, and storage)
      const students = await unifiedStudentStorage.getAllStudents();
      
      // Background sync with Supabase without blocking UI
      studentSupabaseService.syncAllStudents().then(syncedStudents => {
        if (syncedStudents.length > 0) {
          console.log('🔄 Background sync completed from Supabase');
          window.dispatchEvent(new CustomEvent('studentDataChanged'));
        }
      }).catch(error => {
        console.log('Background sync failed, using unified storage data:', error);
      });

      return students;
    } catch (error) {
      console.error('Error in getAllStudents:', error);
      return [];
    }
  },

  getStudent: async (id: number): Promise<Student | undefined> => {
    try {
      const student = await unifiedStudentStorage.getStudentById(id);
      return student || undefined;
    } catch (error) {
      console.error('Error getting student:', error);
      return undefined;
    }
  },

  addStudent: async (student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>): Promise<Student> => {
    try {
      const newStudent = await unifiedStudentStorage.addStudent(student);
      
      // Background sync to Supabase
      try {
        await studentSupabaseService.saveStudent(newStudent);
      } catch (error) {
        console.log('Background sync failed for new student:', error);
      }
      
      console.log('✅ Student added successfully via unified storage:', newStudent);
      return newStudent;
    } catch (error) {
      console.error('Error adding student:', error);
      throw error;
    }
  },

  updateStudent: async (id: number, student: Partial<Omit<Student, 'id' | 'createdAt'>>): Promise<void> => {
    try {
      const updatedStudent = await unifiedStudentStorage.updateStudent(id, student);
      
      // Background sync to Supabase
      if (updatedStudent) {
        try {
          await studentSupabaseService.saveStudent(updatedStudent);
        } catch (error) {
          console.log('Background sync failed for updated student:', error);
        }
      }
      
      console.log('✅ Student updated successfully via unified storage:', updatedStudent);
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  },

  deleteStudent: async (id: number): Promise<void> => {
    try {
      await unifiedStudentStorage.deleteStudent(id);
      
      // Background delete from Supabase
      try {
        await studentSupabaseService.deleteStudent(id);
      } catch (error) {
        console.log('Background delete failed:', error);
      }
      
      console.log(`✅ Student with ID ${id} deleted successfully via unified storage`);
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  },
  
  searchStudents: async (query: string): Promise<Student[]> => {
    try {
      return await unifiedStudentStorage.searchStudents(query);
    } catch (error) {
      console.error('Error searching students:', error);
      return [];
    }
  },
  
  getUpcomingCompletions: async (days: number): Promise<{student: Student, program: Program}[]> => {
    try {
      const students = await unifiedStudentStorage.getAllStudents();
      const upcoming: {student: Student, program: Program}[] = [];
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + days);
      
      students.forEach(student => {
        student.programs?.forEach(program => {
          const completionDate = new Date(program.completionDate);
          if (completionDate <= targetDate && completionDate >= new Date()) {
            upcoming.push({ student, program });
          }
        });
      });
      
      return upcoming;
    } catch (error) {
      console.error('Error getting upcoming completions:', error);
      return [];
    }
  },
  
  getUpcomingPayments: async (days: number): Promise<{student: Student, program: Program}[]> => {
    try {
      const students = await unifiedStudentStorage.getAllStudents();
      const upcoming: {student: Student, program: Program}[] = [];
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + days);
      
      students.forEach(student => {
        student.programs?.forEach(program => {
          if (program.nextPaymentDate) {
            const paymentDate = new Date(program.nextPaymentDate);
            if (paymentDate <= targetDate && paymentDate >= new Date()) {
              upcoming.push({ student, program });
            }
          }
        });
      });
      
      return upcoming;
    } catch (error) {
      console.error('Error getting upcoming payments:', error);
      return [];
    }
  },

  // Teacher operations with hybrid sync
  getAllTeachers: async (): Promise<any[]> => {
    try {
      const localTeachers = JSON.parse(localStorage.getItem('nalc_teachers') || '[]');
      
      teacherSupabaseService.syncAllTeachers().then(syncedTeachers => {
        if (syncedTeachers.length > 0) {
          localStorage.setItem('nalc_teachers', JSON.stringify(syncedTeachers));
          window.dispatchEvent(new CustomEvent('teacherDataChanged'));
        }
      }).catch(error => {
        console.log('Background teacher sync failed, using local data:', error);
      });

      return localTeachers;
    } catch (error) {
      console.error('Error in getAllTeachers:', error);
      return [];
    }
  },

  // Admin operations with localStorage ONLY
  getAllAdmins: async (): Promise<any[]> => {
    try {
      console.log('👥 Loading admins from localStorage ONLY (5000+ capacity)...');
      return await adminFileStorageService.getAllAdmins();
    } catch (error) {
      console.error('Error in getAllAdmins:', error);
      return [];
    }
  },

  // Timetable operations with hybrid sync
  getAllTimetables: async () => {
    try {
      return await timetableFileStorageService.getAllTimetables();
    } catch (error) {
      console.error('Error in getAllTimetables:', error);
      return [];
    }
  },

  getTimetableById: async (id: string) => {
    try {
      return await timetableFileStorageService.getTimetableById(id);
    } catch (error) {
      console.error('Error in getTimetableById:', error);
      return undefined;
    }
  },

  addTimetable: async (timetable: any) => {
    try {
      return await timetableFileStorageService.addTimetable(timetable);
    } catch (error) {
      console.error('Error in addTimetable:', error);
      throw error;
    }
  },

  updateTimetable: async (id: string, timetable: any) => {
    try {
      return await timetableFileStorageService.updateTimetable(id, timetable);
    } catch (error) {
      console.error('Error in updateTimetable:', error);
      throw error;
    }
  },

  deleteTimetable: async (id: string) => {
    try {
      return await timetableFileStorageService.deleteTimetable(id);
    } catch (error) {
      console.error('Error in deleteTimetable:', error);
      throw error;
    }
  },

  getTeacherSyncStatus: (): 'synced' | 'pending' | 'offline' => {
    return teacherSupabaseService.getSyncStatus();
  },

  getStudentSyncStatus: (): 'synced' | 'pending' | 'offline' => {
    return 'synced'; // Unified storage is always synced locally
  },

  getAdminSyncStatus: (): 'synced' | 'pending' | 'offline' => {
    return 'synced';
  },

  getTimetableSyncStatus: (): 'synced' | 'pending' | 'offline' => {
    return timetableSupabaseService.getSyncStatus();
  }
};
